import { useState, useCallback, useMemo } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { Cpu, Wifi, WifiOff, Scan, X, CheckCircle, Info, Lightbulb } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { KeyenceCameraApiClient } from '../api/KeyenceCameraApiClient';
import { AdvancedSettings } from './AdvancedSettings';
import { Skeleton } from '@/components/ui/skeleton';
import { Card } from '@/components/ui/card/Card';
import { Callout } from '@/components/ui/callout';

interface KeyenceScannerCardProps {
  onDetected: (toolId: string) => void;
  isLoading?: boolean;
}

export function KeyenceScannerCard({ onDetected, isLoading = false }: KeyenceScannerCardProps) {
  const [backendUrl, setBackendUrl] = useState<string>('https://localhost:44312');
  const [cameraIp, setCameraIp] = useState<string>('192.168.100.100');
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [isConnecting, setIsConnecting] = useState<boolean>(false);
  const [isScanning, setIsScanning] = useState<boolean>(false);
  const [detectedId, setDetectedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { t } = useTranslation();

  const apiClient = useMemo(() => new KeyenceCameraApiClient(backendUrl), [backendUrl]);

  const handleConnect = useCallback(async () => {
    setIsConnecting(true);
    setError(null);

    try {
      const response = await apiClient.connect(cameraIp);

      if (response.Success && response.Data?.IsConnected) {
        setIsConnected(true);
      } else {
        setError(response.Message || t('traceability.scanPage.keyence.errors.connectionFailed'));
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t('traceability.scanPage.keyence.errors.networkError')
      );
    } finally {
      setIsConnecting(false);
    }
  }, [apiClient, cameraIp, t]);

  const handleDisconnect = useCallback(async () => {
    setIsConnecting(true);
    setError(null);

    try {
      const response = await apiClient.disconnect();

      if (response.Success) {
        setIsConnected(false);
        setDetectedId(null);
      } else {
        setError(response.Message || t('traceability.scanPage.keyence.errors.disconnectionFailed'));
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t('traceability.scanPage.keyence.errors.networkError')
      );
    } finally {
      setIsConnecting(false);
    }
  }, [apiClient, t]);

  const handleScan = useCallback(async () => {
    setIsScanning(true);
    setError(null);
    setDetectedId(null);

    try {
      const response = await apiClient.scan(10000);

      if (response.Success && response.Data?.Success) {
        const rawResult = response.Data.Result || '';
        const toolId = rawResult.replace(/\r/g, '').trim();
        setDetectedId(toolId);

        try {
          await apiClient.disconnect();
          setIsConnected(false);
        } catch (disconnectError) {
          console.warn('Failed to disconnect camera after scan:', disconnectError);
        }
      } else {
        setError(
          response.Data?.ErrorMessage ||
            response.Message ||
            t('traceability.scanPage.keyence.errors.scanFailed')
        );
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t('traceability.scanPage.keyence.errors.networkError')
      );
    } finally {
      setIsScanning(false);
    }
  }, [apiClient, t]);

  const handleCancelScan = useCallback(async () => {
    setError(null);

    try {
      const response = await apiClient.cancelScan(5000);

      if (response.Success) {
        setIsScanning(false);
      } else {
        setError(
          response.Data?.ErrorMessage ||
            response.Message ||
            t('traceability.scanPage.keyence.errors.cancelFailed')
        );
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t('traceability.scanPage.keyence.errors.networkError')
      );
    }
  }, [apiClient, t]);

  const handleAccept = useCallback(() => {
    if (detectedId) {
      onDetected(detectedId);
    }
  }, [detectedId, onDetected]);

  if (isLoading) {
    return (
      <Card as="section" padding="lg" className="flex flex-col gap-6">
        <header className="flex items-start gap-3">
          <Skeleton className="h-10 w-10 rounded-xl" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-full" />
          </div>
        </header>
        <Skeleton className="h-16 rounded-xl" />
        <Skeleton className="h-48 rounded-2xl" />
        <div className="grid gap-3 sm:grid-cols-2">
          <Skeleton className="h-20 rounded-xl" />
          <Skeleton className="h-20 rounded-xl" />
        </div>
      </Card>
    );
  }

  return (
    <Card as="section" padding="lg" className="flex flex-col gap-6">
      <header className="flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary ring-1 ring-primary/20">
          <Cpu className="h-5 w-5" />
        </span>
        <div className="space-y-1">
          <h2 className="section-title">{t('traceability.scanPage.keyence.title')}</h2>
          <p className="text-sm text-muted-foreground">
            {t('traceability.scanPage.keyence.description')}
          </p>
        </div>
      </header>

      <Callout
        tone={isConnected ? 'success' : 'neutral'}
        icon={isConnected ? Wifi : WifiOff}
        className="font-medium"
      >
        {isConnected
          ? t('traceability.scanPage.keyence.status.connected')
          : t('traceability.scanPage.keyence.status.disconnected')}
      </Callout>

      <div className="flex flex-col gap-4">
        <AdvancedSettings
          backendUrl={backendUrl}
          cameraIp={cameraIp}
          onBackendUrlChange={setBackendUrl}
          onCameraIpChange={setCameraIp}
          disabled={isConnected || isConnecting}
        />

        <Button
          variant={isConnected ? 'outline' : 'default'}
          className="w-full"
          onClick={isConnected ? handleDisconnect : handleConnect}
          loading={isConnecting}
        >
          {isConnecting ? (
            isConnected ? (
              t('traceability.scanPage.keyence.disconnecting')
            ) : (
              t('traceability.scanPage.keyence.connecting')
            )
          ) : isConnected ? (
            <>
              <WifiOff aria-hidden />
              {t('traceability.scanPage.keyence.disconnect')}
            </>
          ) : (
            <>
              <Wifi aria-hidden />
              {t('traceability.scanPage.keyence.connect')}
            </>
          )}
        </Button>

        {isConnected && (
          <div className="grid gap-3 sm:grid-cols-2">
            <Button className="w-full" onClick={handleScan} loading={isScanning}>
              {isScanning ? (
                t('traceability.scanPage.keyence.scanning')
              ) : (
                <>
                  <Scan aria-hidden />
                  {t('traceability.scanPage.keyence.scan')}
                </>
              )}
            </Button>

            {isScanning && (
              <Button variant="outline" className="w-full" onClick={handleCancelScan}>
                <X aria-hidden />
                {t('traceability.scanPage.keyence.cancel')}
              </Button>
            )}
          </div>
        )}

        {error && <Callout tone="error">{error}</Callout>}

        {detectedId && (
          <Callout
            tone="success"
            title={t('traceability.scanPage.keyence.detected')}
            action={
              <Button className="w-full" onClick={handleAccept}>
                <CheckCircle aria-hidden />
                {t('traceability.scanPage.keyence.accept')}
              </Button>
            }
          >
            <p className="font-mono text-base font-semibold">{detectedId}</p>
          </Callout>
        )}
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <Callout tone="neutral" icon={Lightbulb}>
          {t('traceability.scanPage.keyence.tips.network')}
        </Callout>
        <Callout tone="neutral" icon={Info}>
          {t('traceability.scanPage.keyence.tips.backend')}
        </Callout>
      </div>
    </Card>
  );
}
